import { ReactComponent as TrashIcon } from 'assets/icons/trash16.svg'
import cx from 'classnames'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { InlineFormatBar } from 'components/InlineFormatBar/InlineFormatBar'
import { InlineFormatButton } from 'components/InlineFormatBar/components/InlineFormatButton/InlineFormatButton'
import { InlineFormatDivider } from 'components/InlineFormatBar/components/InlineFormatDivider/InlineFormatDivider'
import { useEffect, useRef, useState } from 'react'
import { reportError } from 'utils/error'
import { useReactNodeView } from '../ReactNodeView/ReactNodeViewContext'
import { LineDividerDropdownItem } from './LineDividerDropdownItem'
import Styles from './LineDividerFormatBar.module.scss'
import { LINE_STYLES, LineStyle } from './types'

export const LineDividerFormatBar = () => {
  const [chevron, setChevron] = useState<'up' | 'down'>('down')
  const context = useReactNodeView()
  const { node, view, getPos } = context
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Fix bug that closes the format bar on double click
  useEffect(() => {
    const currentRef = wrapperRef.current
    const preventDefault = (event: MouseEvent) => {
      event.preventDefault()
    }

    currentRef?.addEventListener('mousedown', preventDefault)
    return () => {
      currentRef?.removeEventListener('mousedown', preventDefault)
    }
  }, [wrapperRef])

  const setLineDividerStyle = (style: LineStyle) => {
    if (!view || !node) {
      reportError('Error setting line divider style')
      return
    }

    let { tr, selection } = view.state

    view.dispatch(
      tr.setNodeMarkup(selection.from, undefined, {
        ...node.attrs,
        lineStyle: style,
      }),
    )

    view.focus()
  }

  const handleDelete = () => {
    if (!view || !node || !getPos) {
      reportError('Error deleting line divider')
      return
    }

    const {
      dispatch,
      state: { tr },
    } = view

    dispatch(tr.delete(getPos(), getPos() + node.nodeSize))
  }

  const onOpenChange = (isOpen: boolean) => {
    setChevron(isOpen ? 'up' : 'down')
  }

  return (
    <div ref={wrapperRef} className={Styles.formatBarWrapper}>
      <InlineFormatBar className={Styles.InlineFormatBar}>
        <DropdownTrigger popoverClassName={Styles.dropdownContainer} onOpenChange={onOpenChange}>
          <InlineFormatButton
            data-testid="Select line divider style"
            label="Select line divider style"
            className={Styles.dropdownTrigger}
            withChevron={chevron}
          >
            <LineDividerDropdownItem className={Styles.lineDividerDropdownItem} style={node?.attrs.lineStyle} />
          </InlineFormatButton>
          {(close) => {
            return LINE_STYLES.map((style) => {
              const selected = node?.attrs.lineStyle === style

              return (
                <div
                  key={style}
                  aria-label={`Change style to ${style}`}
                  role="button"
                  className={cx(Styles.inlineFormatDropdownItem, Styles.dropdownItem, { [Styles.selected]: selected })}
                  onClick={() => {
                    setLineDividerStyle(style)
                    close()
                  }}
                  data-heap-id={`divider-${style.replaceAll('_', '-')}`}
                >
                  <LineDividerDropdownItem
                    className={Styles.lineDividerDropdownItem}
                    style={style}
                    selected={selected}
                  />
                </div>
              )
            })
          }}
        </DropdownTrigger>
        <InlineFormatDivider />

        <InlineFormatButton data-testid="line-divider-delete" onClick={handleDelete} label="Delete">
          <TrashIcon />
        </InlineFormatButton>
      </InlineFormatBar>
    </div>
  )
}
